import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Check, Copy, Loader2, Plus, QrCode, X } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import CommercialGate from "../../components/commercial/CommercialGate";
import PropertyFields from "../../components/commercial/PropertyFields";
import PropertyPhotos from "../../components/commercial/PropertyPhotos";
import SecurityAccessSection from "../../components/commercial/SecurityAccessSection";
import { api } from "../../lib/api";
import { fmtCents, setPageTitle } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";
import AddressAutocomplete from "../../components/AddressAutocomplete";
import CountryPicker from "../../components/CountryPicker";
import MapPinPicker from "../../components/MapPinPicker";
import { COUNTRIES } from "../../lib/countries";

// Location editing (country + address + map pin) — shares the exact components
// and mismatch rules used by the commercial create flow and individual hosts.
// Country switches invalidate the previous address/pin; a pin reverse-geocoding
// to another country blocks saving until corrected.
function LocationEditor({ prop, onSaved }) {
  const [open, setOpen] = useState(false);
  const [country, setCountry] = useState(prop.country || "");
  const [address, setAddress] = useState(prop.address || "");
  const [region, setRegion] = useState(prop.region || "");
  const [postalCode, setPostalCode] = useState(prop.postal_code || "");
  const [pin, setPin] = useState(prop.lat != null && prop.lng != null ? { lat: prop.lat, lng: prop.lng } : null);
  const [busy, setBusy] = useState(false);
  const [mismatch, setMismatch] = useState(null); // {detected: "US"}
  const countryName = (cc) => COUNTRIES.find(([c]) => c === cc)?.[1] || cc;

  const changeCountry = (c) => {
    if (c === country) return;
    setCountry(c);
    setAddress("");
    setPin(null);
    setMismatch(null);
  };

  const applyLocation = async (lat, lng, addr) => {
    setPin({ lat, lng });
    if (addr) { setAddress(addr); setMismatch(null); return; }
    try {
      const r = await api.geoReverse(lat, lng);
      if (r.formatted_address) setAddress(r.formatted_address);
      if (r.country_code && r.country_code.toUpperCase() !== country) setMismatch({ detected: r.country_code.toUpperCase() });
      else setMismatch(null);
    } catch { /* reverse geocode is best-effort validation */ }
  };

  const onAddressSelect = (s) => {
    if (s.country_code && String(s.country_code).toUpperCase() !== country) {
      setMismatch({ detected: String(s.country_code).toUpperCase() });
      return;
    }
    setMismatch(null);
    setAddress(s.description || [s.main_text, s.secondary_text].filter(Boolean).join(", "));
    if (s.region) setRegion(s.region);
    if (s.postal_code) setPostalCode(s.postal_code);
    if (s.lat != null && s.lng != null) applyLocation(s.lat, s.lng, null);
  };

  const save = async () => {
    if (!country) { toast.error("Choose the country first"); return; }
    if (!address.trim()) { toast.error("Enter the property address"); return; }
    if (!pin) { toast.error("Confirm the exact property location on the map"); return; }
    if (mismatch) {
      toast.error(`This pin is in ${countryName(mismatch.detected)} — switch the country or move the pin before saving.`);
      return;
    }
    setBusy(true);
    try {
      await api.commercialPropertyPatch(prop.property_id, { country, address: address.trim(), lat: pin.lat, lng: pin.lng, region: region || undefined, postal_code: postalCode || undefined });
      toast.success("Location updated");
      setOpen(false);
      onSaved?.(); // refresh from the authoritative saved record
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} data-testid="property-location-edit"
              className="btn-secondary !py-2 text-xs mt-3">Edit location</button>
    );
  }
  return (
    <div className="mt-4 rounded-xl border border-[#27272A] bg-[#0A0D10] p-4 space-y-4" data-testid="property-location-editor">
      <div>
        <label className="block text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500 mb-1.5">Country</label>
        <CountryPicker value={country} onChange={changeCountry} testId="property-edit-country" />
      </div>
      <div>
        <label className="block text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500 mb-1.5">Street address</label>
        <AddressAutocomplete value={address} onChange={setAddress} onSelect={onAddressSelect} country={country || null}
                             placeholder="Start typing the address…" testId="property-edit-address" />
      </div>
      <div>
        <label className="block text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500 mb-1.5">Exact location</label>
        <MapPinPicker lat={pin?.lat} lng={pin?.lng} onPick={(la, ln) => applyLocation(la, ln, null)} testId="property-edit-map" />
        {mismatch && (
          <div className="mt-2 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-300 leading-relaxed"
               data-testid="property-edit-geo-mismatch" role="alert">
            This pin is in <span className="font-semibold">{countryName(mismatch.detected)}</span>, but the selected country is <span className="font-semibold">{countryName(country)}</span>.{" "}
            Move the pin, or{" "}
            <button type="button" className="underline underline-offset-2 font-semibold text-amber-200 hover:text-white transition-colors"
                    data-testid="property-edit-geo-switch"
                    onClick={() => { setCountry(mismatch.detected); setMismatch(null); }}>
              switch the country to {countryName(mismatch.detected)}
            </button>.
          </div>
        )}
      </div>
      <div className="flex gap-3">
        <button type="button" onClick={() => { setOpen(false); setCountry(prop.country || ""); setAddress(prop.address || ""); setPin(prop.lat != null ? { lat: prop.lat, lng: prop.lng } : null); setMismatch(null); }}
                data-testid="property-location-cancel" className="btn-secondary !py-2.5 text-xs">Cancel</button>
        <button type="button" onClick={save} disabled={busy} data-testid="property-location-save"
                className="btn-primary !py-2.5 text-xs disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save location
        </button>
      </div>
    </div>
  );
}

const BASE = process.env.REACT_APP_BACKEND_URL;

// Auth'd binary download (QR/sign endpoints require a session — plain <a href> won't send it)
async function downloadSign(propertyId, cat, fmt) {
  try {
    const r = await fetch(`${BASE}/api/commercial/properties/${propertyId}/sign.${fmt}${cat ? `?category=${cat}` : ""}`, { credentials: "include" });
    if (!r.ok) throw new Error("Sign download failed");
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `myredpark-sign-${fmt}.${fmt}`;
    a.click();
    URL.revokeObjectURL(url);
  } catch (e) { toast.error(e.message); }
}

// Final publication area — the primary red CTA lives at the END of the setup
// page with a live requirements checklist. The server re-validates everything.
function PublicationCard({ prop, busy, publish }) {
  const { user } = useAuth();
  const [acct, setAcct] = useState(null); // null = loading, {error} = fetch failed
  useEffect(() => {
    Promise.all([
      api.commercialVerification().catch(() => "error"),
      api.connectStatus().catch(() => "error"),
    ]).then(([verification, connect]) => setAcct({ verification, connect }));
  }, []);

  const cats = (prop.categories || []).filter((c) => c.active);
  const vErr = !acct || acct.verification === "error";
  const cErr = !acct || acct.connect === "error";
  const checks = [
    ["Property details & location", !!(prop.name && prop.address), false],
    ["At least one property photo", (prop.images || []).length > 0, false],
    ["At least one active category", cats.length > 0, false],
    ["Pricing on every active category", cats.length > 0 && cats.every((c) => (c.rate_plans || []).some((r) => r.active)), false],
    ["Capacity allocation valid", (prop.allocated ?? 0) <= prop.capacity_total, false],
    ["Business verification approved", acct?.verification?.status === "approved", vErr],
    ["Identity verification", !!user?.id_verified, false],
    ["Payout (bank) setup complete", !!acct?.connect?.payouts_enabled, cErr],
  ];
  // Disable only on conditions we KNOW fail; if account status couldn't load,
  // the click goes through and the server answers with the exact reason.
  const blocked = checks.some(([, ok, unknown]) => !ok && !unknown);
  const live = prop.status === "active";

  return (
    <div className="card-dark p-6 border-[#E50914]/25" data-testid="publication-card">
      <p className="section-label !mb-0">Publication</p>
      <div className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2" data-testid="publish-checklist">
        {checks.map(([label, ok, unknown]) => (
          <div key={label} className="flex items-center gap-2 text-xs" data-testid={`publish-check-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <span className={`w-4 h-4 rounded-full flex items-center justify-center shrink-0 ${ok ? "bg-emerald-500/15 text-emerald-400" : "bg-zinc-500/15 text-zinc-600"}`}>
              {ok ? <Check className="w-2.5 h-2.5" /> : null}
            </span>
            <span className={ok ? "text-zinc-300" : "text-zinc-500"}>{label}{unknown && !ok ? " · checking…" : ""}</span>
          </div>
        ))}
      </div>
      <div className="mt-6 flex flex-wrap items-center gap-3">
        {live ? (
          <>
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-400" data-testid="property-published-state"><Check className="w-4 h-4" /> Property published</span>
            <button onClick={publish} disabled={busy} className="btn-secondary !py-2.5 text-xs disabled:opacity-50 ml-auto" data-testid="property-publish-toggle">
              {busy ? "Working…" : "Unpublish property"}
            </button>
          </>
        ) : (
          <>
            {blocked && <p className="text-[11px] text-zinc-600 flex-1 min-w-40">Complete the remaining items — the server re-checks everything at publish time.</p>}
            <button onClick={publish} disabled={busy || blocked}
                    className="btn-primary !py-3 !px-6 text-sm font-bold ml-auto disabled:opacity-50 shadow-lg shadow-[#E50914]/25"
                    data-testid="property-publish-toggle">
              {busy ? (<><Loader2 className="w-4 h-4 animate-spin" /> Publishing Property…</>) : "Publish Property"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function shareProperty(property, cat) {
  const url = `${property.public_url || `${window.location.origin}/p/${property.property_code}`}${cat ? `/${cat}` : ""}`;
  const payload = { title: `${property.name} — MyRedPark`, text: `Park at ${property.name} — code ${property.property_code}`, url };
  if (navigator.share) {
    navigator.share(payload).catch(() => {});
  } else {
    navigator.clipboard?.writeText(url)?.catch(() => {});
    toast.success("Link copied — share it anywhere");
  }
}
const inputCls = "w-full rounded-xl border border-[#27272A] bg-[#0A0D10] px-3.5 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#E50914] transition-colors";
const RATE_KINDS = [["hourly", "Hourly"], ["multi_hour", "Multi-hour"], ["daily", "Daily"], ["flat", "Flat rate"], ["monthly", "Monthly"], ["custom", "Custom"]];

const fmtDuration = (mins) => {
  if (mins % 43200 === 0) return `${mins / 43200} mo`;
  if (mins % 1440 === 0) return `${mins / 1440} day${mins > 1440 ? "s" : ""}`;
  if (mins % 60 === 0) return `${mins / 60} h`;
  return `${mins} min`;
};

function QRCard({ property }) {
  const [qrUrl, setQrUrl] = useState(null);
  const [cat, setCat] = useState("");
  useEffect(() => {
    let url;
    let live = true;
    setQrUrl(null);
    fetch(`${BASE}/api/commercial/properties/${property.property_id}/qr.png${cat ? `?category=${cat}` : ""}`, { credentials: "include" })
      .then((r) => (r.ok ? r.blob() : Promise.reject(new Error("qr"))))
      .then((b) => { url = URL.createObjectURL(b); if (live) setQrUrl(url); })
      .catch(() => {});
    return () => { live = false; if (url) URL.revokeObjectURL(url); };
  }, [property.property_id, cat]);

  const publicUrl = property.public_url || `${window.location.origin}/p/${property.property_code}`;
  return (
    <div id="qr" className="card-dark p-6" data-testid="property-qr-card">
      <p className="meta-label">Property code &amp; QR</p>
      <div className="mt-4 flex flex-col items-start gap-5 sm:flex-row sm:items-center sm:gap-6">
        <div className="text-center">
          <p className="font-mono font-extrabold text-4xl tracking-[0.2em] text-[#FF3B45] break-all" data-testid="property-code-display">{property.property_code}</p>
          <p className="text-[9px] uppercase tracking-widest text-zinc-600 mt-1">4-digit code</p>
        </div>
        <div className="w-28 h-28 rounded-xl bg-white p-2 flex items-center justify-center shrink-0" data-testid="property-qr-image">
          {qrUrl ? <img src={qrUrl} alt={`QR code linking to ${publicUrl}`} className="w-full h-full" /> : <Loader2 className="w-5 h-5 animate-spin text-zinc-500" />}
        </div>
        <div className="min-w-0 flex-1 space-y-2.5">
          <div className="flex items-center gap-2 min-w-0">
            <code className="text-xs text-zinc-300 bg-white/[0.04] rounded-lg px-2.5 py-1.5 min-w-0 break-all" data-testid="property-public-url">{publicUrl}{cat ? `/${cat}` : ""}</code>
            <button onClick={() => { navigator.clipboard?.writeText(`${publicUrl}${cat ? `/${cat}` : ""}`)?.catch(() => {}); toast.success("Link copied"); }}
                    className="p-1.5 text-zinc-500 hover:text-white transition-colors" aria-label="Copy link" data-testid="property-copy-url">
              <Copy className="w-3.5 h-3.5" />
            </button>
          </div>
          {property.categories?.length > 0 && (
            <select value={cat} onChange={(e) => setCat(e.target.value)} className={inputCls} data-testid="qr-category-select" aria-label="QR category">
              <option value="">Whole property</option>
              {property.categories.map((c) => <option key={c.category_id} value={c.code}>{c.name}</option>)}
            </select>
          )}
          {qrUrl && (
            <a href={qrUrl} download={`myredpark-property-${property.property_code}${cat ? `-${cat.toLowerCase()}` : ""}.png`}
               className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#FF3B45] hover:text-white transition-colors" data-testid="property-qr-download">
              <QrCode className="w-3.5 h-3.5" /> Download QR (print-ready PNG)
            </a>
          )}
          <div className="flex flex-wrap gap-2 pt-1">
            <button onClick={() => downloadSign(property.property_id, cat, "pdf")}
                    className="btn-secondary !py-2 !px-3 text-xs" data-testid="sign-download-pdf">
              Download sign (PDF)
            </button>
            <button onClick={() => downloadSign(property.property_id, cat, "png")}
                    className="btn-secondary !py-2 !px-3 text-xs" data-testid="sign-download-png">
              Sign (PNG)
            </button>
            <button onClick={() => shareProperty(property, cat)}
                    className="btn-ghost-red !py-2 !px-3 text-xs" data-testid="property-share">
              Share
            </button>
          </div>
          <p className="text-[11px] text-zinc-600">{cat ? "Category sign resolves directly to this category." : "Property sign resolves to the whole property."} Print at A6/A5/A4 — the QR stays scannable.</p>
          <p className="text-[11px] text-zinc-600">Drivers scan this QR or enter the code on the homepage to park at this property.</p>
        </div>
      </div>
    </div>
  );
}

function RateForm({ onAdd, busy }) {
  const [kind, setKind] = useState("hourly");
  const [dur, setDur] = useState("");
  const [amount, setAmount] = useState("");
  const [renew, setRenew] = useState(false);
  const [winStart, setWinStart] = useState("");
  const [winEnd, setWinEnd] = useState("");
  const [days, setDays] = useState([]);
  const needsDur = kind === "multi_hour" || kind === "flat" || kind === "custom";
  const windowed = !!(winStart && winEnd);
  const toggleDay = (d) => setDays((ds) => (ds.includes(d) ? ds.filter((x) => x !== d) : [...ds, d].sort()));
  const submit = () => {
    onAdd({
      kind,
      duration_minutes: needsDur && dur ? parseInt(dur, 10) : undefined,
      amount_cents: Math.round(parseFloat(amount || "0") * 100),
      auto_renew_eligible: renew,
      active: true,
      window_start: windowed ? winStart : undefined,
      window_end: windowed ? winEnd : undefined,
      days_of_week: windowed && days.length ? days : undefined,
    }, () => { setAmount(""); setDur(""); setRenew(false); setWinStart(""); setWinEnd(""); setDays([]); });
  };
  return (
    <div className="mt-3 space-y-2.5">
      <div className="flex flex-wrap items-end gap-2">
        <select value={kind} onChange={(e) => setKind(e.target.value)} className={`${inputCls} !w-auto`} aria-label="Rate kind" data-testid="rate-kind">
          {RATE_KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        {needsDur && (
          <input type="number" min={15} value={dur} onChange={(e) => setDur(e.target.value)} placeholder="Minutes"
                 className={`${inputCls} !w-28`} aria-label="Duration in minutes" data-testid="rate-duration" />
        )}
        <input type="number" min={0.5} step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Price"
               className={`${inputCls} !w-28`} aria-label="Price" data-testid="rate-amount" />
        {kind === "monthly" && (
          <label className="flex items-center gap-1.5 text-xs text-zinc-400 pb-2.5">
            <input type="checkbox" checked={renew} onChange={(e) => setRenew(e.target.checked)} data-testid="rate-autorenew" /> Auto-renew
          </label>
        )}
        <button type="button" disabled={busy} onClick={submit}
                className="btn-primary !py-2.5 !px-4 text-xs disabled:opacity-50" data-testid="rate-add">
          <Plus className="w-3.5 h-3.5" /> Add rate
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-2" data-testid="rate-window">
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500">Time window (optional)</span>
        <input type="time" value={winStart} onChange={(e) => setWinStart(e.target.value)} aria-label="Window start"
               className={`${inputCls} !w-28 !py-1.5 text-xs`} data-testid="rate-window-start" />
        <span className="text-zinc-600 text-xs">→</span>
        <input type="time" value={winEnd} onChange={(e) => setWinEnd(e.target.value)} aria-label="Window end"
               className={`${inputCls} !w-28 !py-1.5 text-xs`} data-testid="rate-window-end" />
        {windowed && (
          <div className="flex gap-1" role="group" aria-label="Days of week">
            {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
              <button type="button" key={i} onClick={() => toggleDay(i)} data-testid={`rate-day-${i}`}
                      aria-pressed={days.includes(i)} aria-label={`Day ${["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][i]}`}
                      className={`w-7 h-7 rounded-lg text-[10px] font-bold transition-colors ${days.includes(i) ? "bg-[#E50914] text-white" : "border border-[#27272A] text-zinc-500 hover:text-zinc-300"}`}>
                {d}
              </button>
            ))}
          </div>
        )}
        {windowed && <p className="w-full text-[10px] text-zinc-600">Applies {winStart}–{winEnd}{days.length ? ` on ${days.length} selected day(s)` : " daily"} (property local time). Overlapping windows are rejected by the server.</p>}
      </div>
    </div>
  );
}

function CategoryCard({ prop, cat, onChange }) {
  const [cap, setCap] = useState(String(cat.capacity));
  const [busy, setBusy] = useState(false);

  const saveCap = async () => {
    const v = parseInt(cap, 10);
    if (!v || v === cat.capacity) return;
    setBusy(true);
    try {
      await api.commercialCategoryPatch(prop.property_id, cat.category_id, { capacity: v });
      toast.success(`${cat.name} capacity → ${v}`);
      onChange();
    } catch (e) { toast.error(e.message); setCap(String(cat.capacity)); }
    finally { setBusy(false); }
  };
  const toggle = async () => {
    setBusy(true);
    try {
      await api.commercialCategoryPatch(prop.property_id, cat.category_id, { active: !cat.active });
      onChange();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  const addRate = async (body, reset) => {
    if (!body.amount_cents) { toast.error("Enter a price"); return; }
    setBusy(true);
    try {
      await api.commercialRateCreate(prop.property_id, cat.category_id, body);
      toast.success("Rate added");
      reset();
      onChange();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  const toggleRate = async (r) => {
    try {
      await api.commercialRatePatch(prop.property_id, r.rate_plan_id, { kind: r.kind, duration_minutes: r.duration_minutes, amount_cents: r.amount_cents, auto_renew_eligible: r.auto_renew_eligible, active: !r.active });
      onChange();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="card-dark p-5" data-testid={`category-card-${cat.code}`}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-display font-bold">{cat.name}</p>
            <span className="font-mono text-[10px] text-zinc-500 bg-white/[0.04] px-1.5 py-0.5 rounded" data-testid={`category-code3-${cat.code}`}>{cat.code3 ? `${prop.property_code}-${cat.code3}` : cat.code}</span>
            {!cat.active && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border bg-amber-500/15 text-amber-400 border-amber-500/30">inactive</span>}
          </div>
          {cat.description && <p className="text-xs text-zinc-500 mt-1">{cat.description}</p>}
          <p className="text-[11px] text-zinc-600 mt-1.5">{cat.spots_active} live spots · {cat.spots_inactive} inactive</p>
        </div>
        <div className="flex items-center gap-2">
          <input type="number" min={1} value={cap} onChange={(e) => setCap(e.target.value)} className={`${inputCls} !w-24`} aria-label={`${cat.name} capacity`} data-testid={`category-capacity-${cat.code}`} />
          <button onClick={saveCap} disabled={busy} className="btn-secondary !py-2 !px-3 text-xs disabled:opacity-50" data-testid={`category-save-cap-${cat.code}`}>Save</button>
          <button onClick={toggle} disabled={busy} className={`text-xs font-semibold px-2 py-2 disabled:opacity-50 ${cat.active ? "text-amber-400" : "text-emerald-400"}`} data-testid={`category-toggle-${cat.code}`}>
            {cat.active ? "Deactivate" : "Activate"}
          </button>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {cat.rate_plans.map((r) => (
          <span key={r.rate_plan_id} className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border ${r.active ? "border-white/10 text-zinc-200" : "border-white/5 text-zinc-600 line-through"}`} data-testid={`rate-${r.rate_plan_id}`}>
            {RATE_KINDS.find(([k]) => k === r.kind)?.[1] || r.kind} · {fmtDuration(r.duration_minutes)} · {fmtCents(r.amount_cents, r.currency)}
            {r.window_start && <span className="text-[9px] uppercase text-zinc-500 font-bold" data-testid={`rate-window-badge-${r.rate_plan_id}`}>{r.window_start}–{r.window_end}{r.days_of_week?.length ? ` · ${r.days_of_week.length}d` : ""}</span>}
            {r.auto_renew_eligible && <span className="text-[9px] uppercase text-[#FF3B45] font-bold">auto</span>}
            <button onClick={() => toggleRate(r)} className="ml-1 text-zinc-500 hover:text-white" aria-label={r.active ? "Deactivate rate" : "Activate rate"} data-testid={`rate-toggle-${r.rate_plan_id}`}>
              {r.active ? <X className="w-3 h-3" /> : <Check className="w-3 h-3" />}
            </button>
          </span>
        ))}
        {cat.rate_plans.length === 0 && <span className="text-xs text-zinc-600">No rates yet — add at least one to publish.</span>}
      </div>
      <RateForm onAdd={addRate} busy={busy} />
    </div>
  );
}

function DiscountsSection({ prop, onChange }) {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState({ code: "", discount_percent: "", category_id: "", max_redemptions: "", ends_at: "" });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.commercialDiscounts(prop.property_id).then((r) => setRows(r.discounts)).catch(() => setRows([])), [prop.property_id]);
  useEffect(() => { load(); }, [load]);
  const toggle = async (d, action) => {
    try {
      await api.commercialDiscountPatch(prop.property_id, d.promotion_id, action);
      load();
    } catch (err) { toast.error(err.message); }
  };
  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.commercialDiscountCreate(prop.property_id, {
        ...(form.code.trim() ? { code: form.code.trim() } : {}),
        discount_percent: parseFloat(form.discount_percent),
        ...(form.category_id ? { category_id: form.category_id } : {}),
        ...(form.max_redemptions ? { max_redemptions: parseInt(form.max_redemptions, 10) } : {}),
        ...(form.ends_at ? { ends_at: new Date(form.ends_at).toISOString() } : {}),
      });
      toast.success("Discount created");
      setForm({ code: "", discount_percent: "", category_id: "", max_redemptions: "", ends_at: "" });
      load();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  return (
    <div>
      <p className="section-label">Discounts</p>
      <div className="card-dark p-5 mt-3" data-testid="discounts-section">
        <div className="space-y-2">
          {(rows || []).map((d) => (
            <div key={d.promotion_id} className="flex flex-wrap items-center gap-3 text-xs rounded-xl bg-white/[0.03] px-3.5 py-2.5" data-testid={`discount-${d.code}`}>
              <span className="font-mono font-bold text-[#FF3B45]">{d.code}</span>
              <span className="font-semibold">{d.discount_percent}% off</span>
              <span className="text-zinc-500">{d.category_id ? "category-scoped" : "all categories"}{d.rate_plan_id ? " · plan-scoped" : ""}</span>
              <span className="text-zinc-600">{d.redemptions_count || 0}{d.max_redemptions ? `/${d.max_redemptions}` : ""} used</span>
              <button onClick={() => toggle(d, d.status === "active" ? "deactivate" : "activate")}
                      className={`ml-auto font-semibold ${d.status === "active" ? "text-amber-400" : "text-emerald-400"}`}
                      data-testid={`discount-toggle-${d.code}`}>
                {d.status === "active" ? "Deactivate" : "Activate"}
              </button>
            </div>
          ))}
          {rows && rows.length === 0 && <p className="text-xs text-zinc-600">No discounts yet.</p>}
          {!rows && <div className="h-9 rounded-xl bg-white/[0.03] animate-pulse" />}
        </div>
        <form onSubmit={create} className="mt-4 flex flex-wrap items-end gap-2" data-testid="discount-create-form">
          <input value={form.code} onChange={(e) => setForm((s) => ({ ...s, code: e.target.value.toUpperCase() }))}
                 placeholder="CODE (auto)" className={`${inputCls} !w-32`} data-testid="discount-code" aria-label="Discount code" />
          <input required type="number" min={1} max={90} step="0.5" value={form.discount_percent}
                 onChange={(e) => setForm((s) => ({ ...s, discount_percent: e.target.value }))}
                 placeholder="% off" className={`${inputCls} !w-24`} data-testid="discount-percent" aria-label="Discount percent" />
          <select value={form.category_id} onChange={(e) => setForm((s) => ({ ...s, category_id: e.target.value }))}
                  className={`${inputCls} !w-40`} data-testid="discount-category" aria-label="Discount category scope">
            <option value="">All categories</option>
            {prop.categories.map((c) => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
          </select>
          <input type="number" min={1} value={form.max_redemptions}
                 onChange={(e) => setForm((s) => ({ ...s, max_redemptions: e.target.value }))}
                 placeholder="Usage cap" className={`${inputCls} !w-28`} data-testid="discount-cap" aria-label="Usage cap" />
          <button type="submit" disabled={busy} className="btn-primary !py-2.5 !px-4 text-xs disabled:opacity-50" data-testid="discount-create">
            <Plus className="w-3.5 h-3.5" /> Create
          </button>
        </form>
      </div>
    </div>
  );
}

function AnnouncementsSection({ prop }) {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState({ title: "", message: "" });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.commercialAnnouncements(prop.property_id).then((r) => setRows(r.announcements)).catch(() => setRows([])), [prop.property_id]);
  useEffect(() => { load(); }, [load]);
  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.commercialAnnouncementCreate(prop.property_id, form);
      toast.success(`Announcement published — ${r.notified_drivers} eligible driver${r.notified_drivers === 1 ? "" : "s"} notified`);
      setForm({ title: "", message: "" });
      load();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  return (
    <div>
      <p className="section-label">Announcements</p>
      <div className="card-dark p-5 mt-3" data-testid="announcements-section">
        <div className="space-y-2">
          {(rows || []).map((a) => (
            <div key={a.announcement_id} className={`flex flex-wrap items-center gap-3 text-xs rounded-xl px-3.5 py-2.5 ${a.active ? "bg-white/[0.03]" : "opacity-50"}`} data-testid={`announcement-${a.announcement_id}`}>
              <div className="min-w-0 flex-1">
                <p className="font-semibold">{a.title}</p>
                <p className="text-zinc-500 mt-0.5 truncate">{a.message}</p>
              </div>
              <span className="text-zinc-600">{new Date(a.created_at).toLocaleDateString()}</span>
              <button onClick={async () => { try { await api.commercialAnnouncementPatch(prop.property_id, a.announcement_id, a.active ? "deactivate" : "activate"); load(); } catch (e) { toast.error(e.message); } }}
                      className={`font-semibold ${a.active ? "text-amber-400" : "text-emerald-400"}`} data-testid={`announcement-toggle-${a.announcement_id}`}>
                {a.active ? "Deactivate" : "Activate"}
              </button>
            </div>
          ))}
          {rows && rows.length === 0 && <p className="text-xs text-zinc-600">No announcements yet.</p>}
          {!rows && <div className="h-9 rounded-xl bg-white/[0.03] animate-pulse" />}
        </div>
        <form onSubmit={create} className="mt-4 space-y-2" data-testid="announcement-create-form">
          <input required maxLength={120} value={form.title} onChange={(e) => setForm((s) => ({ ...s, title: e.target.value }))}
                 placeholder="Title — e.g. Entrance temporarily moved" className={inputCls} data-testid="announcement-title" aria-label="Announcement title" />
          <textarea required maxLength={1000} rows={2} value={form.message} onChange={(e) => setForm((s) => ({ ...s, message: e.target.value }))}
                    placeholder="Message for drivers parking at this property…" className={inputCls} data-testid="announcement-message" aria-label="Announcement message" />
          <button type="submit" disabled={busy} className="btn-primary !py-2.5 !px-4 text-xs disabled:opacity-50" data-testid="announcement-create">
            <Plus className="w-3.5 h-3.5" /> Publish announcement
          </button>
          <p className="text-[10px] text-zinc-600">Only drivers with active or upcoming bookings at this property are notified.</p>
        </form>
      </div>
    </div>
  );
}

function DetailBody({ id }) {
  const [prop, setProp] = useState(null);
  const [error, setError] = useState("");
  const [spots, setSpots] = useState(null);
  const [spotPage, setSpotPage] = useState(1);
  const [newCat, setNewCat] = useState({ name: "", capacity: "" });
  const [capEdit, setCapEdit] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.commercialProperty(id).then(setProp).catch((e) => setError(e.message)), [id]);
  const loadSpots = useCallback((p = 1) => api.commercialSpots(id, `?page=${p}`).then((r) => { setSpots(r); setSpotPage(p); }).catch(() => {}), [id]);
  useEffect(() => { setPageTitle("Property"); load(); loadSpots(1); }, [load, loadSpots]);
  useEffect(() => { if (prop) setCapEdit(String(prop.capacity_total)); }, [prop]);

  if (error) return <div className="card-dark p-8 text-center text-sm text-[#FF6B6B]" role="alert">{error}</div>;
  if (!prop) return <div className="card-dark h-64 animate-pulse" />;

  const addCategory = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.commercialCategoryCreate(id, { name: newCat.name, capacity: parseInt(newCat.capacity, 10) });
      setNewCat({ name: "", capacity: "" });
      toast.success("Category created — spots generated automatically");
      load();
      loadSpots(1);
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };
  const saveCapacity = async () => {
    const v = parseInt(capEdit, 10);
    if (!v || v === prop.capacity_total) return;
    setBusy(true);
    try {
      await api.commercialPropertyPatch(id, { capacity_total: v });
      toast.success(`Property capacity → ${v}`);
      load();
    } catch (e) { toast.error(e.message); setCapEdit(String(prop.capacity_total)); }
    finally { setBusy(false); }
  };
  const publish = async () => {
    setBusy(true);
    try {
      if (prop.status === "active") {
        await api.commercialPropertyUnpublish(id);
        toast.success("Property unpublished");
      } else {
        await api.commercialPropertyPublish(id);
        toast.success("Property published — drivers can now find and book it.");
      }
      load();
    } catch (e) {
      if (e.message === "IDENTITY_VERIFICATION_REQUIRED") {
        toast.error("Identity verification required before publishing", {
          action: { label: "Verify now", onClick: () => { window.location.href = "/profile"; } },
        });
      } else toast.error(e.message);
    }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div className="card-dark p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="font-display font-bold text-2xl" data-testid="property-detail-name">{prop.name}</h2>
            <p className="text-sm text-zinc-500 mt-1" data-testid="property-detail-location">{prop.address} · {prop.country} · {prop.timezone}</p>
            {prop.instructions && <p className="text-xs text-zinc-600 mt-2 max-w-lg">{prop.instructions}</p>}
            <LocationEditor prop={prop} onSaved={load} />
          </div>
          <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${prop.status === "active" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : "bg-zinc-500/15 text-zinc-400 border-zinc-500/30"}`} data-testid="property-detail-status">{prop.status}</span>
        </div>
        <div className="mt-5 flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500 mb-1.5">Total capacity</label>
            <input type="number" min={1} max={10000} value={capEdit} onChange={(e) => setCapEdit(e.target.value)} className={`${inputCls} !w-28`} data-testid="property-capacity-edit" />
          </div>
          <button onClick={saveCapacity} disabled={busy} className="btn-secondary !py-2.5 text-xs disabled:opacity-50" data-testid="property-capacity-save">Save capacity</button>
          <p className="text-xs text-zinc-500 pb-2.5">Allocated to categories: <span className="text-white font-semibold" data-testid="property-allocated">{prop.allocated}</span></p>
        </div>
      </div>

      <QRCard property={prop} />

      <PropertyPhotos prop={prop} onChange={load} />

      <SecurityAccessSection propertyId={prop.property_id} />

      <div>
        <p className="section-label">Categories &amp; pricing</p>
        <div className="mt-3 space-y-3">
          {prop.categories.map((c) => <CategoryCard key={c.category_id} prop={prop} cat={c} onChange={() => { load(); loadSpots(spotPage); }} />)}
        </div>
        <form onSubmit={addCategory} className="card-dark p-5 mt-3 flex flex-wrap items-end gap-3" data-testid="category-add-form">
          <div className="flex-1 min-w-40">
            <label className="block text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500 mb-1.5">New category</label>
            <input required minLength={2} maxLength={60} value={newCat.name} onChange={(e) => setNewCat((s) => ({ ...s, name: e.target.value }))}
                   placeholder="Visitors" className={inputCls} data-testid="category-name" />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500 mb-1.5">Spots</label>
            <input required type="number" min={1} value={newCat.capacity} onChange={(e) => setNewCat((s) => ({ ...s, capacity: e.target.value }))}
                   placeholder="10" className={`${inputCls} !w-24`} data-testid="category-capacity" />
          </div>
          <button type="submit" disabled={busy} className="btn-primary !py-2.5 text-xs disabled:opacity-50" data-testid="category-add">
            <Plus className="w-3.5 h-3.5" /> Add category
          </button>
        </form>
      </div>

      <PropertyFields propertyId={prop.property_id} categories={prop.categories} />

      <div className="grid md:grid-cols-2 gap-6">
        <DiscountsSection prop={prop} onChange={load} />
        <AnnouncementsSection prop={prop} />
      </div>

      <div>
        <p className="section-label">Spot inventory {spots ? `(${spots.total})` : ""}</p>
        <div className="card-dark mt-3 overflow-hidden" data-testid="spots-table">
          <div className="max-h-72 overflow-auto">
            <table className="w-full text-xs min-w-0">
              <thead className="sticky top-0 bg-[#0D1114]">
                <tr className="text-left text-zinc-500 uppercase tracking-wider text-[10px]">
                  <th className="px-4 py-2.5">Spot ref</th><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5">Created</th>
                </tr>
              </thead>
              <tbody>
                {(spots?.spots || []).map((s) => (
                  <tr key={s.spot_ref} className="border-t border-white/[0.05]">
                    <td className="px-4 py-2 font-mono">{s.spot_ref}</td>
                    <td className="px-4 py-2"><span className={s.status === "active" ? "text-emerald-400" : "text-zinc-500"}>{s.status}</span></td>
                    <td className="px-4 py-2 text-zinc-500">{new Date(s.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {spots && spots.total > 100 && (
            <div className="flex items-center justify-between px-4 py-2.5 border-t border-white/[0.05] text-xs text-zinc-500">
              <button disabled={spotPage <= 1} onClick={() => loadSpots(spotPage - 1)} className="disabled:opacity-40 hover:text-white" data-testid="spots-prev">← Prev</button>
              <span>Page {spotPage} of {Math.ceil(spots.total / 100)}</span>
              <button disabled={spotPage * 100 >= spots.total} onClick={() => loadSpots(spotPage + 1)} className="disabled:opacity-40 hover:text-white" data-testid="spots-next">Next →</button>
            </div>
          )}
        </div>
      </div>

      <PublicationCard prop={prop} busy={busy} publish={publish} />
    </div>
  );
}

export default function PropertyDetail() {
  const { id } = useParams();
  return (
    <CommercialGate>
      <div className="min-h-screen flex flex-col" data-testid="property-detail-page">
        <Navbar />
        <main className="flex-1 max-w-7xl w-full mx-auto px-5 sm:px-8 pt-28 pb-16">
          <Link to="/commercial/properties" className="text-xs text-zinc-500 hover:text-white transition-colors inline-flex items-center gap-1.5">
            <ArrowLeft className="w-3.5 h-3.5" /> All properties
          </Link>
          <div className="mt-5"><DetailBody id={id} /></div>
        </main>
        <Footer />
      </div>
    </CommercialGate>
  );
}
